// Chrome for the environments (§12.3: "chrome (pinball, sweeper steel)"): a painted equirect
// environment per map — its sky gradient with soft cloud bands, a hot sun spot where the key light
// sits, and the void's color below the horizon — so polished metal (the teaspoon, the cake forks,
// the drum, the porthole) mirrors its own diorama instead of a neutral studio.
import { Color, EquirectangularReflectionMapping, MeshStandardMaterial, type ColorRepresentation, type Texture, type Vector3 } from 'three';
import { canvasTexture, type Ctx2D } from './textures';

export interface ChromeSky {
  top: ColorRepresentation;
  bottom: ColorRepresentation;
  /** The void's main color (the lower hemisphere). */
  ground: ColorRepresentation;
  /** Optional accent painted as a thin horizon stripe (neon on dark maps). */
  accent?: ColorRepresentation;
}

const css = (c: ColorRepresentation, k = 1): string => {
  const col = new Color(c).multiplyScalar(k);
  return `rgb(${Math.round(Math.min(1, col.r) * 255)}, ${Math.round(Math.min(1, col.g) * 255)}, ${Math.round(Math.min(1, col.b) * 255)})`;
};

/** Paints the environment: equirect u = azimuth, v = elevation (top = up). */
function paintSky(g: Ctx2D, w: number, h: number, sky: ChromeSky, sun: Vector3): void {
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, css(sky.top, 1.05));
  grad.addColorStop(0.46, css(sky.bottom, 1.08));
  grad.addColorStop(0.5, css('#FFFFFF'));
  grad.addColorStop(0.53, css(sky.ground, 1.0));
  grad.addColorStop(1, css(sky.ground, 0.55));
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Soft cloud bands above the horizon.
  g.globalAlpha = 0.35;
  for (let i = 0; i < 9; i++) {
    const x = ((i * 0.37) % 1) * w;
    const y = h * (0.18 + ((i * 0.53) % 1) * 0.24);
    const cg = g.createRadialGradient(x, y, 0, x, y, h * 0.16);
    cg.addColorStop(0, 'rgba(255,255,255,0.9)');
    cg.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = cg;
    g.fillRect(x - h * 0.3, y - h * 0.16, h * 0.6, h * 0.32);
  }
  g.globalAlpha = 1;
  if (sky.accent) {
    g.fillStyle = css(sky.accent);
    g.fillRect(0, h * 0.47, w, Math.max(2, h * 0.012));
  }
  // The key light: a hot spot plus a wide glow (azimuth of the light in three.js x/z).
  const az = Math.atan2(sun.x, -sun.z);
  const el = Math.asin(Math.max(-1, Math.min(1, sun.y)));
  const sx = ((az / (Math.PI * 2)) + 0.5) * w;
  const sy = (0.5 - el / Math.PI) * h;
  for (const dx of [-w, 0, w]) {
    const glow = g.createRadialGradient(sx + dx, sy, 0, sx + dx, sy, h * 0.3);
    glow.addColorStop(0, 'rgba(255,250,235,1)');
    glow.addColorStop(0.12, 'rgba(255,246,225,0.9)');
    glow.addColorStop(1, 'rgba(255,240,220,0)');
    g.fillStyle = glow;
    g.fillRect(sx + dx - h * 0.3, sy - h * 0.3, h * 0.6, h * 0.6);
  }
}

/** The map's environment texture (equirect, sRGB). */
export function skyEnvTexture(sky: ChromeSky, sun: Vector3): Texture {
  const t = canvasTexture(512, 256, (g, w, h) => paintSky(g, w, h, sky, sun));
  t.mapping = EquirectangularReflectionMapping;
  return t;
}

/** Polished metal tinted by vertex colors (steel, gold), mirroring the map's environment. */
export function chromeMaterial(env: Texture, o: { roughness?: number; intensity?: number; vertexColors?: boolean; color?: ColorRepresentation } = {}): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color: o.color ?? '#FFFFFF', vertexColors: o.vertexColors ?? true, metalness: 1, roughness: o.roughness ?? 0.22,
    envMap: env, envMapIntensity: o.intensity ?? 1.0,
  });
}
